import { EMPTY_VALUE, type Money, formatDate } from '@betterment/core';
import { useGraphQLQuery } from '@betterment/graphql';
import { Box, Card, H2, HStack, Icon, Separator, Text, VStack } from '@betterment/samba-react';

import { ErrorBoundary } from '~/b4b/components/error-boundary/ErrorBoundary';
import { SambaAutoLink } from '~/b4b/components/SambaAutoLink';
import { useCurrentEmployerId } from '~/b4b/utils/currentEmployer';
import { formatMoneySafe } from '~/b4b/utils/formatHelpers';
import { urlBuilder } from '~/b4b/utils/urlBuilder';
import { PayrollIntegrationStatus } from '~/graphql/schema';

import { PayrollDocument } from './graphql/query.payroll.generated';
import { LoadingPayrollCard } from './LoadingPayrollCard';

const INTEGRATION_STATUS_CONFIG = {
  [PayrollIntegrationStatus.Connected]: {
    icon: 'CheckmarkCircleFilled' as const,
    color: 'icon.success' as const,
    label: 'Integration connected',
    size: 'xsmall' as const,
  },
  [PayrollIntegrationStatus.Disconnected]: {
    icon: 'WarningFilled' as const,
    color: 'icon.warning' as const,
    label: 'Integration disconnected',
    size: 'xsmall' as const,
  },
};

type IntegrationStatusRowProps = {
  integrationStatus: PayrollIntegrationStatus;
  isReconnectionRequired: boolean;
};

const IntegrationStatusRow = ({ integrationStatus, isReconnectionRequired }: IntegrationStatusRowProps) => {
  if (
    integrationStatus === PayrollIntegrationStatus.AwaitingSetup ||
    integrationStatus === PayrollIntegrationStatus.SetupInProgress
  ) {
    return (
      <HStack css={{ justifyContent: 'space-between', alignItems: 'center' }}>
        <Text variant="text100">Integration status</Text>
        {integrationStatus === PayrollIntegrationStatus.AwaitingSetup ? (
          <Text variant="text100">Awaiting setup</Text>
        ) : (
          <HStack css={{ gap: 'space.50', alignItems: 'center' }}>
            <Icon name="ArrowCircle" size="xxsmall" />
            <Text variant="text100">Pending</Text>
          </HStack>
        )}
      </HStack>
    );
  }

  const config = INTEGRATION_STATUS_CONFIG[integrationStatus];

  return (
    <VStack>
      <HStack css={{ gap: 'space.50', alignItems: 'center' }}>
        <Text variant="text100">{config.label}</Text>
        <Icon name={config.icon} color={config.color} size={config.size} />
      </HStack>
      {isReconnectionRequired && <SambaAutoLink to={urlBuilder.payroll()}>Reconnect</SambaAutoLink>}
    </VStack>
  );
};

type AutoApproveRowProps = {
  isAutoApprovalEnabled: boolean;
  integrationStatus: string | null | undefined;
  pendingApprovalCount: number;
};

const AutoApproveRow = ({ isAutoApprovalEnabled, integrationStatus, pendingApprovalCount }: AutoApproveRowProps) => {
  const isDisconnected = integrationStatus === PayrollIntegrationStatus.Disconnected;

  return isAutoApprovalEnabled && !isDisconnected ? (
    <HStack css={{ gap: 'space.50', alignItems: 'center' }}>
      <Text variant="text100">Auto-approve payrolls</Text>
      <Icon name="CheckmarkCircleFilled" color="icon.success" size="xsmall" />
    </HStack>
  ) : (
    <HStack css={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
      <VStack>
        <Text variant="text100">Payrolls pending approval</Text>
        {isDisconnected && (
          <Text variant="text50" color="text.weak">
            Auto-approve off
          </Text>
        )}
      </VStack>
      <Text variant="text100">{pendingApprovalCount > 0 ? pendingApprovalCount : EMPTY_VALUE}</Text>
    </HStack>
  );
};

type LastPayrollRowProps = {
  amount: Money;
  effectiveOn: Date;
};

const LastPayrollRow = ({ amount, effectiveOn }: LastPayrollRowProps) => (
  <HStack css={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
    <VStack>
      <Text variant="text100">Last 401(k) payroll</Text>
      <Text variant="text50" color="text.weak">
        Effective {formatDate(effectiveOn, 'longOrdinalSlashed')}
      </Text>
    </VStack>
    <Text variant="text100">{formatMoneySafe(amount)}</Text>
  </HStack>
);

const PayrollCardContent = () => {
  const employerId = useCurrentEmployerId();
  const { data } = useGraphQLQuery(PayrollDocument, { variables: { employerId }, throwOnError: true });

  const overview = data?.employer?.payrollOverview;

  if (!data || !overview) {
    return (
      <Card css={{ p: 'space.300' }}>
        <LoadingPayrollCard />
      </Card>
    );
  }

  const isSetupStatus =
    overview.integrationStatus === PayrollIntegrationStatus.AwaitingSetup ||
    overview.integrationStatus === PayrollIntegrationStatus.SetupInProgress;
  const hasIntegrationStatus =
    !!overview.integrationStatus &&
    (isSetupStatus ||
      overview.autoApprovalEnabled ||
      overview.integrationStatus === PayrollIntegrationStatus.Disconnected);

  return (
    <Card css={{ p: 'space.300' }}>
      <Card.Header
        css={{ pb: 'space.300', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}
      >
        <VStack>
          <H2 variant="title200">Payroll</H2>
          {overview.payrollProviderName && <Text variant="text50">{overview.payrollProviderName}</Text>}
        </VStack>
        <SambaAutoLink css={{ display: { base: 'none', lg: 'block' } }} to={urlBuilder.payroll()}>
          View details
        </SambaAutoLink>
      </Card.Header>

      <Separator />

      <VStack css={{ pt: 'space.300', gap: 'space.200' }}>
        {hasIntegrationStatus && (
          <IntegrationStatusRow
            integrationStatus={overview.integrationStatus!}
            isReconnectionRequired={overview.isReconnectionRequired ?? false}
          />
        )}

        {!isSetupStatus && (
          <AutoApproveRow
            isAutoApprovalEnabled={overview.autoApprovalEnabled ?? false}
            integrationStatus={overview.integrationStatus}
            pendingApprovalCount={overview.pendingApprovalCount ?? 0}
          />
        )}

        {overview.lastPayroll && (
          <LastPayrollRow
            amount={overview.lastPayroll.amount as Money}
            effectiveOn={overview.lastPayroll.effectiveOn!}
          />
        )}

        <Box css={{ display: { base: 'block', lg: 'none' }, width: '100%' }}>
          <Separator css={{ mb: 'space.200' }} />
          <SambaAutoLink to={urlBuilder.payroll()}>View details</SambaAutoLink>
        </Box>
      </VStack>
    </Card>
  );
};

export const PayrollCard = () => (
  <ErrorBoundary name="payrollCard" fallbackType="inline">
    <PayrollCardContent />
  </ErrorBoundary>
);
